import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axiosInstance from '../../axiosConfig';

// Guard-rail: the endpoint paginates by 100, so 100 pages bounds the loop to
// ~10k stores and prevents an infinite loop if the API keeps returning `next`.
const MAX_PAGES = 100;

const fetchStores = async (params) => {
  const data_results = [];
  const initialOffset = Number(params.offset ?? 0);
  const baseParams = { ...params };
  let response;
  let page = 0;

  do {
    const offset = initialOffset + data_results.length;
    response = await axiosInstance.get('/stores/', {
      params: { ...baseParams, offset },
    });
    for (let result of response.data.results) {
      data_results.push(result);
    }
    page += 1;
  } while (response.data?.next && response.status === 200 && page < MAX_PAGES);
  if (response.status === 200) {
    response.data.results = data_results;
    response.data.next = null;
    response.data.previous = null;
  }
  return response.data;
};

export const useGetStores = (params = {}, options = {}) => {
  const queryParams = useMemo(() => {
    const out = {};
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') out[k] = v;
    });
    return out;
  }, [params]);

  const { data, error, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['stores', queryParams],
    queryFn: () => fetchStores(queryParams),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
  });

  const stores = data?.results;
  const count = data?.count;
  const next = data?.next;
  const previous = data?.previous;

  return {
    stores,
    count,
    next,
    previous,
    error,
    isLoading,
    isError,
    isFetching,
    refetch,
  };
};
